import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LoginServicePayload,
  LoginServiceResponse,
  LoginWithOTPServicePayload,
  LoginWithOTPServiceResponse,
  ValidateOTPServicePayload,
  ValidateOTPServiceResponse,
  EmailVerificaitonServicePayload,
  EmailVerifcationServiceResponse,
  GetMyTeamResponse,
  UpdateAccountServicePayload,
  UserImageServicePayload,
  MyProfileResponse,
  ProfessionalDescriptionItem,
  ClearSsoSessionServiceResponse,
  AccountLogoutServiceResponse,
} from "interfaces";
import {
  loginService,
  loginWithOTPService,
  validateOTPService,
  emailVerificationService,
  getProfilePictureService,
  getMyTeamService,
  getMemberImageService,
  getUserImageService,
  updateProfilePictureService,
  updateAccountService,
  getMyProfileService,
  getProfessionalDescriptionService,
  getTeamMembersService,
  clearSsoSessionService,
  accountLogoutService,
} from "services";

export const useLogin = (callBack: (data: LoginServiceResponse) => void) => {
  return useMutation({
    mutationFn: (payload: LoginServicePayload) => loginService(payload),
    onSuccess: (data) => {
      callBack(data);
    },
  });
};

export const useValidateOTP = (
  callBack: (data: ValidateOTPServiceResponse) => void,
) => {
  return useMutation({
    mutationFn: (payload: ValidateOTPServicePayload) =>
      validateOTPService(payload),
    onSuccess: (data) => {
      callBack(data);
    },
  });
};

export const useLoginWithOTP = (
  callBack: (data: LoginWithOTPServiceResponse) => void,
) => {
  return useMutation({
    mutationFn: (payload: LoginWithOTPServicePayload) =>
      loginWithOTPService(payload),
    onSuccess: (data) => {
      callBack(data);
    },
  });
};

export const useEmailVerification = (
  callBack: (data: EmailVerifcationServiceResponse) => void,
) => {
  return useMutation({
    mutationFn: (payload: EmailVerificaitonServicePayload) =>
      emailVerificationService(payload),
    onSuccess: (data) => {
      callBack(data);
    },
  });
};

export const useClearSsoSession = () => {
  return useMutation<ClearSsoSessionServiceResponse, Error>({
    mutationFn: () => clearSsoSessionService(),
  });
};

export const useAccountLogout = () => {
  return useMutation<AccountLogoutServiceResponse, Error>({
    mutationFn: () => accountLogoutService(),
  });
};

export const useClearSessionAndLogout = () => {
  return useMutation<
    {
      clearSsoSession: ClearSsoSessionServiceResponse;
      logout: AccountLogoutServiceResponse;
    },
    Error
  >({
    mutationFn: async () => {
      const [clearSsoSession, logout] = await Promise.all([
        clearSsoSessionService(),
        accountLogoutService(),
      ]);
      return { clearSsoSession, logout };
    },
  });
};

export const useGetProfilePicture = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["getProfilePicture"],
    queryFn: () => getProfilePictureService(),
    enabled,
  });
};

export const useGetMemberImage = (memberId: string) => {
  return useQuery<string>({
    queryKey: ["member-image", memberId],
    queryFn: () => getMemberImageService(memberId),
    enabled: !!memberId,
    staleTime: Infinity,
  });
};

export const useGetUserImage = (payLoad: UserImageServicePayload) => {
  return useQuery({
    queryKey: ["getUserImage", payLoad.id, payLoad.params?.speakerProfile],
    queryFn: () => getUserImageService(payLoad),
    // Only hit the endpoint once we actually have an id; otherwise the request
    // fires with an empty id and 404s on the initial render.
    enabled: !!payLoad.id,
  });
};

export const useUpdateProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => updateProfilePictureService(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getProfilePicture"] });
    },
  });
};

export const useUpdateAccount = (callBack?: () => void) => {
  return useMutation({
    mutationFn: (payload: UpdateAccountServicePayload) =>
      updateAccountService(payload),
    onSuccess: () => {
      callBack?.();
    },
  });
};

export const useGetMyProfile = (enabled: boolean = true) => {
  return useQuery<MyProfileResponse>({
    queryKey: ["myProfile"],
    queryFn: () => getMyProfileService(),
    enabled,
  });
};

export const useGetProfessionalDescription = (enabled: boolean = true) => {
  return useQuery<ProfessionalDescriptionItem[]>({
    queryKey: ["professionalDescription"],
    queryFn: () => getProfessionalDescriptionService(),
    enabled,
  });
};

export const useGetMyTeam = (enabled: boolean = true) => {
  return useQuery<GetMyTeamResponse>({
    queryKey: ["my-team"],
    queryFn: () => getMyTeamService(),
    enabled,
  });
};

export const useGetTeamMembers = (enabled: boolean = true) => {
  return useQuery<any[]>({
    queryKey: ["team-members"],
    queryFn: () => getTeamMembersService(),
    enabled,
  });
};
